import { countBy } from "lodash";
import { telemetry } from "@/utilities/telemetry";
import { getHoursUntil } from "utilities/datetime";
import type { BetSelection } from "@/features/betslip/types";
import type {
  AcceptedBetResponse,
  AnalyticsContext,
  BetResponse,
  BetslipStoreHandle,
  RejectedBetResponse,
  SubmitBetParams,
} from "@/features/betslip/services/submitBet/types";

export const calculatePayout = (
  stakeCents: number,
  odds: number,
  isPromo: boolean,
): number => {
  const grossDollars = (stakeCents * odds) / 100 / 100;
  const stakeDollars = stakeCents / 100;
  return isPromo ? grossDollars - stakeDollars : grossDollars;
};

type BuildContextInput = {
  store: ReturnType<BetslipStoreHandle["getState"]>;
  params: SubmitBetParams;
  stakedSingles: Array<[string, number]>;
  multiLegCorrelationIds: Record<string, string>;
  hasMultiStake: boolean;
};

export const buildAnalyticsContext = ({
  store,
  params,
  stakedSingles,
  multiLegCorrelationIds,
  hasMultiStake,
}: BuildContextInput): AnalyticsContext => {
  const { currency, profile } = params;

  // Singles sorted ascending by stake. The first entry is the "first
  // single" used in single-bet telemetry fields.
  const stakedSingleSelections = stakedSingles
    .map(([id]) => store.selections[id])
    .filter((s): s is BetSelection => Boolean(s))
    .sort(
      (a, b) =>
        (store.stakes.singles[a.outcomeId] ?? 0) -
        (store.stakes.singles[b.outcomeId] ?? 0),
    );

  const isMulti = hasMultiStake;
  const combinedOdds = isMulti
    ? store.multiLegs.reduce((acc, l) => acc * l.odds, 1)
    : stakedSingleSelections[0]?.odds ?? 0;
  const multiPayoutDollars = isMulti
    ? calculatePayout(store.stakes.multi, combinedOdds, store.promoCash.multi)
    : 0;

  // Bet Submitted title/market fields carry EVENT names, not market
  // names — dashboards group on them that way.
  const allSelections = Object.values(store.selections);
  const eventNameCounts = countBy(allSelections, (s) => s.eventName ?? "");
  const commonEventTitle = Object.keys(eventNameCounts).reduce(
    (prev, curr) =>
      (eventNameCounts[prev] ?? 0) > (eventNameCounts[curr] ?? 0) ? prev : curr,
    "none",
  );
  const firstMultiLegEventName = store.multiLegs[0]?.eventName ?? "";

  const firstStartTime =
    stakedSingleSelections[0]?.startTime ??
    store.multiLegs[0]?.startTime ??
    new Date();
  const startTimeISO =
    firstStartTime instanceof Date
      ? firstStartTime.toISOString()
      : new Date(firstStartTime).toISOString();
  const hoursBeforeStart = Math.round(
    getHoursUntil(
      firstStartTime instanceof Date
        ? firstStartTime
        : new Date(firstStartTime),
    ),
  );

  // Origin: pick the first non-BETTING selection so conversions credit
  // the originating feature (pickems, betbuilder, etc) even if other
  // legs were added manually afterwards.
  const originSelection = allSelections.find(
    (s) => s.originType && s.originType !== "BETTING",
  );
  const origin = originSelection
    ? {
        type: originSelection.originType ?? "BETTING",
        id: originSelection.originId,
      }
    : { type: "BETTING", id: undefined };

  // correlationId → outcomeId reverse lookup for per-leg multi rejections.
  const correlationToOutcome: Record<string, string> = {};
  for (const [outcomeId, corr] of Object.entries(multiLegCorrelationIds)) {
    correlationToOutcome[corr] = outcomeId;
  }

  // Combo correlation/total lookups for combo response telemetry.
  const comboCorrelationToType: Record<string, string> = {};
  const comboTotalsByType: Record<string, number> = {};
  for (const [type, combo] of Object.entries(store.stakes.combinations)) {
    if (!combo) continue;
    if (combo.correlationId) comboCorrelationToType[combo.correlationId] = type;
    comboTotalsByType[type] = combo.total ?? 0;
  }

  return {
    isMulti,
    betType: isMulti ? "MULTI" : "SINGLE",
    combinedOdds,
    multiPayoutDollars,
    commonEventTitle,
    firstMultiLegEventName,
    startTimeISO,
    hoursBeforeStart,
    origin,
    currency,
    profile,
    stakedSingles: stakedSingleSelections,
    correlationToOutcome,
    comboCorrelationToType,
    comboTotalsByType,
  };
};

type FireContext = {
  storeHandle: BetslipStoreHandle;
  analytics: AnalyticsContext;
};

type BetShape = {
  title: string;
  market: string;
  stake: number;
  odds: number;
  estPayout: number;
  startTime: string;
};

// Stakes go to Segment in DOLLARS; the store keeps cents.
const comboShape = (comboType: string, stakeCents: number): BetShape => ({
  title: `Any ${comboType}`,
  market: `Any ${comboType}`,
  stake: stakeCents / 100,
  odds: 0,
  estPayout: 0,
  startTime: new Date().toISOString(),
});

const multiShape = (
  analytics: AnalyticsContext,
  multiStakeCents: number,
): BetShape => ({
  title: analytics.commonEventTitle,
  market: analytics.firstMultiLegEventName,
  stake: multiStakeCents / 100,
  odds: analytics.combinedOdds,
  estPayout: analytics.multiPayoutDollars,
  startTime: analytics.startTimeISO,
});

const singleShape = (
  firstSingle: BetSelection | undefined,
  stakeCents: number,
  isPromo: boolean,
  analytics: AnalyticsContext,
): BetShape => ({
  title: firstSingle?.eventName ?? "",
  market: firstSingle?.eventName ?? "",
  stake: stakeCents / 100,
  odds: firstSingle?.odds ?? 0,
  estPayout: firstSingle
    ? calculatePayout(stakeCents, firstSingle.odds, isPromo)
    : 0,
  startTime: analytics.startTimeISO,
});

/**
 * Per-message telemetry payload. Fires once per accepted or rejected
 * response. `bet-intercepted` doesn't emit here; its follow-up message
 * emits when it resolves.
 */
export const fireBetSubmittedTelemetry = (
  resp: AcceptedBetResponse | RejectedBetResponse,
  outcome: "Accepted" | "Rejected",
  context: FireContext,
): void => {
  const { analytics } = context;
  const correlationId = resp.data.correlationId;
  const comboType = analytics.comboCorrelationToType[correlationId];
  const isCombo = !!comboType;

  const firstSingle = analytics.stakedSingles[0];
  const store = context.storeHandle.getState();
  const firstSingleStakeCents = firstSingle
    ? store.stakes.singles[firstSingle.outcomeId] ?? 0
    : 0;
  const firstSinglePromo = firstSingle
    ? store.promoCash.singles[firstSingle.outcomeId] ?? false
    : false;

  const shape = isCombo
    ? comboShape(comboType, analytics.comboTotalsByType[comboType] ?? 0)
    : analytics.isMulti
      ? multiShape(analytics, store.stakes.multi)
      : singleShape(
          firstSingle,
          firstSingleStakeCents,
          firstSinglePromo,
          analytics,
        );

  // entryId only exists once a bet is placed; rejections omit the key.
  const entryId =
    resp.code === "bet-accepted" || resp.code === "bet-partial-stake-accepted"
      ? resp.data.entryId
      : undefined;

  telemetry.betSubmitted({
    entryId,
    // Combos are a multi-tab product — their events always report MULTI.
    type: isCombo ? "MULTI" : analytics.betType,
    legs: resp.data.outcomeIds.length,
    currency: analytics.currency,
    hoursBeforeStart: analytics.hoursBeforeStart,
    outcome,
    isComboMulti: isCombo,
    originType: analytics.origin.type,
    originId: analytics.origin.id,
    email: analytics.profile.email,
    name: `${analytics.profile.firstName} ${analytics.profile.lastName}`,
    ...shape,
  });
};
